import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useToast } from '../../components/ui/Toast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import Shell from '../dashboard/Shell';
import Modal from '../../components/ui/Modal';
import { navigateToUpgrade } from '../subscription/upgradeNav';
import { getStoreUrl } from '../../config/platformConfig';
import {
  Megaphone,
  TrendingUp,
  Users,
  Settings,
  BarChart3,
  Plus,
  Edit,
  Trash2,
  Play,
  Pause,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Image,
  Type,
  CheckCircle,
  AlertCircle,
  Lock,
  X,
  Copy,
  Download,
  Send,
  Smartphone,
  Tablet,
  Monitor,
  Copy as CopyIcon,
  Shield,
  AlertTriangle,
  FileText,
  Loader2,
} from 'lucide-react';
import './MerchantAds.css';

const PILL_FOR_STATUS = {
  active:    'ma-pill-success',
  scheduled: 'ma-pill-info',
  paused:    'ma-pill-warn',
  draft:     'ma-pill-muted',
  expired:   'ma-pill-muted',
  cancelled: 'ma-pill-muted',
};

// =========================================
// MAIN COMPONENT
// =========================================
const MerchantAds = () => {
  const navigate = useNavigate();
  const { getAxios } = useSecureAuth();
  const { accountCurrency } = useCurrency();
  const toast = useToast();
  const { confirm } = useConfirm();
  const store = useSelector(state => state.store?.current);

  // State
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  // Data states
  const [subscription, setSubscription] = useState(null);
  const [config, setConfig] = useState(null);
  const [clients, setClients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [settings, setSettings] = useState(null);
  const [pricingTable, setPricingTable] = useState(null);
  const [bannerSizes, setBannerSizes] = useState([]);

  // Modal states
  const [clientModal, setClientModal] = useState({ open: false, client: null });
  const [campaignModal, setCampaignModal] = useState({ open: false, campaign: null });
  const [templateModal, setTemplateModal] = useState({ open: false, campaignId: null, campaignName: '' });
  const [templateNameInput, setTemplateNameInput] = useState('');

  // Bulk selection state
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);

  // Templates state
  const [templates, setTemplates] = useState([]);

  // Fraud alerts state (read-only - settings controlled by admin)
  const [fraudAlerts, setFraudAlerts] = useState([]);
  const [fraudSummary, setFraudSummary] = useState(null);

  // Fraud status (flagged/suspended)
  const [fraudStatus, setFraudStatus] = useState({
    isFlagged: false,
    isSuspended: false,
    flagReason: null,
    flaggedAt: null,
    suspendedReason: null,
    suspendedAt: null
  });

  // Tier-based access info
  const [access, setAccess] = useState({
    hasAccess: false,
    hasTierAccess: false,
    hasSubscription: false,
    merchantTier: 'FREE',
    isTeamMember: false
  });

  // Pricing modal for upgrade

  // Billing info from actual tier subscription
  const [billing, setBilling] = useState(null);

  // Appeal modal
  const [appealModal, setAppealModal] = useState(false);
  const [appealForm, setAppealForm] = useState({ reason: '', details: '' });
  const [appealLoading, setAppealLoading] = useState(false);

  // Collapsible settings cards
  const [collapsedCards, setCollapsedCards] = useState({
    subscription: false,
    blurb: false,
    pricing: true, // Start collapsed
    placements: false,
    bannerSizes: true
  });

  const toggleCard = (card) => {
    setCollapsedCards(prev => ({ ...prev, [card]: !prev[card] }));
  };

  // Fetch initial data
  useEffect(() => {
    fetchSubscription();
  }, []);

  useEffect(() => {
    // Fetch data if user has access (via tier or subscription)
    if (access.hasAccess) {
      fetchData();
    }
  }, [access.hasAccess, activeTab]);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const axios = getAxios();
      const response = await axios.get('/merchant/ads/status');
      setSubscription(response.data.subscription);
      setConfig(response.data.config);
      // Set access info
      if (response.data.access) {
        setAccess(response.data.access);
      }
      // Set billing info from actual tier subscription
      if (response.data.billing) {
        setBilling(response.data.billing);
      }
      // Set fraud status if available
      if (response.data.fraudStatus) {
        setFraudStatus(response.data.fraudStatus);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const axios = getAxios();

      if (activeTab === 'overview') {
        const [clientsRes, campaignsRes] = await Promise.all([
          axios.get('/merchant/ads/clients?limit=5'),
          axios.get('/merchant/ads/campaigns?limit=5')
        ]);
        setClients(clientsRes.data.clients || []);
        setCampaigns(campaignsRes.data.campaigns || []);
      } else if (activeTab === 'clients') {
        const response = await axios.get('/merchant/ads/clients');
        setClients(response.data.clients || []);
      } else if (activeTab === 'campaigns') {
        const [campaignsRes, sizesRes, templatesRes] = await Promise.all([
          axios.get('/merchant/ads/campaigns'),
          axios.get('/merchant/ads/banner-sizes'),
          axios.get('/merchant/ads/templates')
        ]);
        setCampaigns(campaignsRes.data.campaigns || []);
        setBannerSizes(sizesRes.data.sizes || []);
        setTemplates(templatesRes.data.templates || []);
        setSelectedCampaigns([]); // Clear selection when refreshing
      } else if (activeTab === 'analytics') {
        const [analyticsRes, fraudAlertsRes, fraudSummaryRes] = await Promise.all([
          axios.get('/merchant/ads/analytics'),
          axios.get('/merchant/ads/fraud/alerts'),
          axios.get('/merchant/ads/fraud/summary')
        ]);
        setAnalytics(analyticsRes.data.analytics);
        setFraudAlerts(fraudAlertsRes.data.alerts || []);
        setFraudSummary(fraudSummaryRes.data.summary || null);
      } else if (activeTab === 'settings') {
        const [settingsRes, pricingRes] = await Promise.all([
          axios.get('/merchant/ads/settings'),
          axios.get('/merchant/ads/pricing-table')
        ]);
        setSettings(settingsRes.data.settings);
        setPricingTable(pricingRes.data.pricingTable);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    }
  };

  // Client handlers
  const handleSaveClient = async (clientData) => {
    setActionLoading('client');
    try {
      const axios = getAxios();
      if (clientModal.client?.id) {
        await axios.put(`/merchant/ads/clients/${clientModal.client.id}`, clientData);
        toast.success('Client updated');
      } else {
        await axios.post('/merchant/ads/clients', clientData);
        toast.success('Client added');
      }
      setClientModal({ open: false, client: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save client');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteClient = async (clientId) => {
    const confirmed = await confirm('This will also delete all campaigns for this client.', {
      title: 'Delete Client?',
      confirmText: 'Delete',
      type: 'danger'
    });
    if (!confirmed) return;

    try {
      const axios = getAxios();
      await axios.delete(`/merchant/ads/clients/${clientId}`);
      toast.success('Client deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete client');
    }
  };

  // Campaign handlers
  const handleSaveCampaign = async (campaignData) => {
    setActionLoading('campaign');
    try {
      const axios = getAxios();
      if (campaignModal.campaign?.id) {
        await axios.put(`/merchant/ads/campaigns/${campaignModal.campaign.id}`, campaignData);
        toast.success('Campaign updated');
      } else {
        await axios.post('/merchant/ads/campaigns', campaignData);
        toast.success('Campaign created');
      }
      setCampaignModal({ open: false, campaign: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save campaign');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCampaignAction = async (campaignId, action) => {
    try {
      const axios = getAxios();
      await axios.post(`/merchant/ads/campaigns/${campaignId}/${action}`);
      toast.success(`Campaign ${action === 'activate' ? 'activated' : 'paused'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || `Failed to ${action} campaign`);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    const confirmed = await confirm('Are you sure you want to delete this campaign?', {
      title: 'Delete Campaign?',
      confirmText: 'Delete',
      type: 'danger'
    });
    if (!confirmed) return;

    try {
      const axios = getAxios();
      await axios.delete(`/merchant/ads/campaigns/${campaignId}`);
      toast.success('Campaign deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete campaign');
    }
  };

  // Duplicate campaign handler
  const handleDuplicateCampaign = async (campaignId) => {
    try {
      setActionLoading('duplicate-' + campaignId);
      const axios = getAxios();
      await axios.post(`/merchant/ads/campaigns/${campaignId}/duplicate`);
      toast.success('Campaign duplicated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to duplicate campaign');
    } finally {
      setActionLoading(null);
    }
  };

  // Bulk selection handlers
  const handleSelectCampaign = (campaignId) => {
    setSelectedCampaigns(prev =>
      prev.includes(campaignId)
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  const handleSelectAll = () => {
    const selectableCampaigns = campaigns.filter(c => !c.isTemplate);
    if (selectedCampaigns.length === selectableCampaigns.length) {
      setSelectedCampaigns([]);
    } else {
      setSelectedCampaigns(selectableCampaigns.map(c => c.id));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedCampaigns.length === 0) return;

    const actionLabels = {
      pause: 'pause',
      resume: 'resume',
      delete: 'delete'
    };

    if (action === 'delete') {
      const confirmed = await confirm(`Are you sure you want to delete ${selectedCampaigns.length} campaign(s)?`, {
        title: 'Delete Campaigns?',
        confirmText: 'Delete All',
        type: 'danger'
      });
      if (!confirmed) return;
    }

    try {
      setActionLoading('bulk-' + action);
      const axios = getAxios();
      const response = await axios.post('/merchant/ads/campaigns/bulk-action', {
        campaignIds: selectedCampaigns,
        action
      });
      toast.success(response.data.message || `${response.data.affected} campaign(s) ${actionLabels[action]}d`);
      setSelectedCampaigns([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || `Failed to ${actionLabels[action]} campaigns`);
    } finally {
      setActionLoading(null);
    }
  };

  // Open template name modal
  const handleSaveAsTemplate = (campaignId, campaignName) => {
    setTemplateNameInput(`${campaignName} Template`);
    setTemplateModal({ open: true, campaignId, campaignName });
  };

  // Actually save the template
  const handleConfirmSaveTemplate = async () => {
    if (!templateNameInput.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    try {
      setActionLoading('template-' + templateModal.campaignId);
      const axios = getAxios();
      await axios.post('/merchant/ads/templates', {
        campaignId: templateModal.campaignId,
        templateName: templateNameInput.trim()
      });
      toast.success('Template saved');
      setTemplateModal({ open: false, campaignId: null, campaignName: '' });
      setTemplateNameInput('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save template');
    } finally {
      setActionLoading(null);
    }
  };

  // Delete template
  const handleDeleteTemplate = async (templateId) => {
    const confirmed = await confirm('Are you sure you want to delete this template?', {
      title: 'Delete Template?',
      confirmText: 'Delete',
      type: 'danger'
    });
    if (!confirmed) return;

    try {
      const axios = getAxios();
      await axios.delete(`/merchant/ads/templates/${templateId}`);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete template');
    }
  };

  // Dismiss fraud alert
  const handleDismissFraudAlert = async (alertId) => {
    try {
      const axios = getAxios();
      await axios.post(`/merchant/ads/fraud/${alertId}/dismiss`);
      toast.success('Alert dismissed');
      setFraudAlerts(prev => prev.filter(a => a.id !== alertId));
      if (fraudSummary) {
        setFraudSummary(prev => ({ ...prev, activeAlerts: Math.max(0, prev.activeAlerts - 1) }));
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to dismiss alert');
    }
  };

  // Handle appeal submission
  const handleSubmitAppeal = async () => {
    if (!appealForm.reason.trim()) {
      toast.error('Please provide a reason for your appeal');
      return;
    }
    try {
      setAppealLoading(true);
      const axios = getAxios();
      await axios.post('/merchant/ads/appeal', {
        reason: appealForm.reason,
        details: appealForm.details
      });
      toast.success('Appeal submitted successfully. We will review and respond within 2-3 business days.');
      setAppealModal(false);
      setAppealForm({ reason: '', details: '' });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit appeal');
    } finally {
      setAppealLoading(false);
    }
  };

  // Format helpers
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  // Loading state
  if (loading) {
    return (
      <Shell>
        <div className="ma-page">
          <div className="ma-loading">
            <Loader2 size={20} className="ma-spin" /> Loading…
          </div>
        </div>
      </Shell>
    );
  }

  // Suspension / flagged Notice — admin-controlled lockout
  if (fraudStatus.isSuspended || fraudStatus.isFlagged) {
    const isSuspended = fraudStatus.isSuspended;
    const reason = isSuspended ? fraudStatus.suspendedReason : fraudStatus.flagReason;
    const dateStr = isSuspended
      ? (fraudStatus.suspendedAt ? new Date(fraudStatus.suspendedAt).toLocaleDateString() : 'N/A')
      : (fraudStatus.flaggedAt ? new Date(fraudStatus.flaggedAt).toLocaleDateString() : 'N/A');

    return (
      <Shell>
        <div className="ma-page">
          <div className="ma-state">
            <div className={`ma-state-icon ${isSuspended ? 'is-danger' : 'is-warn'}`}>
              <AlertTriangle size={22} />
            </div>
            <h2>{isSuspended ? 'Ad access suspended' : 'Ad account flagged'}</h2>
            <p className="ma-state-desc">
              {reason || 'Your ad account has been flagged for suspicious activity.'}
            </p>
            <p className="ma-state-desc">
              {isSuspended
                ? 'Suspensions can only be lifted by our admin team. Please contact support if you believe this was done in error.'
                : 'You cannot create new campaigns, activate paused campaigns, or make changes until the flag is resolved.'}
            </p>
            <span className="ma-state-meta">
              {isSuspended ? 'Suspended on' : 'Flagged on'}: {dateStr}
            </span>
            <div className="ma-state-actions">
              {!isSuspended && (
                <button
                  type="button"
                  className="ma-btn ma-btn-primary"
                  onClick={() => setAppealModal(true)}
                >
                  Submit appeal
                </button>
              )}
              <a href="mailto:support@userevstar.com" className="ma-btn ma-btn-secondary">
                Contact support
              </a>
            </div>
          </div>

          {/* Appeal Modal */}
          <Modal
            isOpen={appealModal}
            onClose={() => setAppealModal(false)}
            title="Submit appeal"
            size="md"
            padded={false}
            footer={
              <>
                <button
                  type="button"
                  className="ma-btn ma-btn-secondary"
                  onClick={() => setAppealModal(false)}
                  disabled={appealLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="ma-appeal-form"
                  className="ma-btn ma-btn-primary"
                  disabled={!appealForm.reason || appealLoading}
                >
                  {appealLoading ? <Loader2 size={14} className="ma-spin" /> : null}
                  {appealLoading ? 'Submitting…' : 'Submit appeal'}
                </button>
              </>
            }
          >
            <form
              id="ma-appeal-form"
              className="ma-form"
              onSubmit={(e) => { e.preventDefault(); handleSubmitAppeal(); }}
            >
              <p className="ma-form-help">
                If you believe your account was flagged in error, please provide details below.
                Our team will review your appeal and respond within 2–3 business days.
              </p>
              <div className="ma-field">
                <label className="ma-field-label">Reason for appeal *</label>
                <select
                  className="ma-input ma-select"
                  value={appealForm.reason}
                  onChange={e => setAppealForm(prev => ({ ...prev, reason: e.target.value }))}
                  required
                >
                  <option value="">Select a reason…</option>
                  <option value="false_positive">False positive — I didn't engage in fraud</option>
                  <option value="technical_issue">Technical issue caused suspicious activity</option>
                  <option value="third_party">Third party accessed my account</option>
                  <option value="misunderstanding">Policy misunderstanding</option>
                  <option value="other">Other reason</option>
                </select>
              </div>
              <div className="ma-field">
                <label className="ma-field-label">Additional details</label>
                <textarea
                  className="ma-input ma-textarea"
                  value={appealForm.details}
                  onChange={e => setAppealForm(prev => ({ ...prev, details: e.target.value }))}
                  placeholder="Provide any additional context or evidence to support your appeal…"
                  rows={5}
                />
              </div>
            </form>
          </Modal>
        </div>
      </Shell>
    );
  }

  // Feature not available — tier upgrade prompt
  if (!access.hasAccess) {
    return (
      <Shell>
        <div className="ma-page">
          <div className="ma-state">
            <div className="ma-state-icon is-brand">
              <Megaphone size={22} />
            </div>
            <h2>Storefront Ads</h2>
            <p className="ma-state-desc">
              Earn additional revenue by allowing other businesses to place ads on your storefront.
              Manage ad clients, set your pricing, and track your earnings from ad placements.
            </p>
            <span className="ma-state-meta">
              Current plan: <strong>{access.merchantTier || 'FREE'}</strong>
              {access.isTeamMember && ' · contact your store owner to upgrade'}
            </span>
            <div className="ma-state-actions">
              {access.isTeamMember ? (
                <span className="ma-team-notice">Only store owners can upgrade plans</span>
              ) : (
                <button
                  type="button"
                  className="ma-btn ma-btn-primary"
                  onClick={() => navigateToUpgrade(navigate)}
                >
                  <Lock size={14} /> View pricing & upgrade
                </button>
              )}
            </div>
          </div>

        </div>
      </Shell>
    );
  }

  // =========================================
  // RENDER TABS
  // =========================================

  const renderOverview = () => {
    const activeCampaigns = campaigns.filter(c => c.status === 'ACTIVE');
    const totalViews = campaigns.reduce((sum, c) => sum + (c.totalViews || 0), 0);
    const totalClicks = campaigns.reduce((sum, c) => sum + (c.totalClicks || 0), 0);
    return (
      <>
        <section className="ma-stat-grid">
          <div className="ma-stat">
            <span className="ma-stat-label">Ad clients</span>
            <span className="ma-stat-value">{clients.length}</span>
          </div>
          <div className="ma-stat">
            <span className="ma-stat-label">Active campaigns</span>
            <span className="ma-stat-value ma-stat-value-success">{activeCampaigns.length}</span>
          </div>
          <div className="ma-stat">
            <span className="ma-stat-label">Total views</span>
            <span className="ma-stat-value">{formatNumber(totalViews)}</span>
          </div>
          <div className="ma-stat">
            <span className="ma-stat-label">Total clicks</span>
            <span className="ma-stat-value">{formatNumber(totalClicks)}</span>
          </div>
        </section>

        <div className="ma-overview-grid">
          <section className="ma-card">
            <div className="ma-card-head">
              <h2 className="ma-card-title">Recent clients</h2>
              <button type="button" className="ma-btn ma-btn-ghost" onClick={() => setActiveTab('clients')}>
                View all <ChevronRight size={12} />
              </button>
            </div>
            {clients.length === 0 ? (
              <div className="ma-empty-inline">
                <Users size={20} />
                <span>No clients yet</span>
                <button
                  type="button"
                  className="ma-btn ma-btn-secondary"
                  onClick={() => setClientModal({ open: true, client: null })}
                >
                  <Plus size={13} /> Add client
                </button>
              </div>
            ) : (
              <ul className="ma-mini-list">
                {clients.slice(0, 5).map(client => (
                  <li key={client.id}>
                    <span className="ma-avatar">{client.companyName?.charAt(0).toUpperCase() || '?'}</span>
                    <span className="ma-mini-text">
                      <strong>{client.companyName}</strong>
                      <em>{client._count?.campaigns || 0} campaign{client._count?.campaigns === 1 ? '' : 's'}</em>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="ma-card">
            <div className="ma-card-head">
              <h2 className="ma-card-title">Active campaigns</h2>
              <button type="button" className="ma-btn ma-btn-ghost" onClick={() => setActiveTab('campaigns')}>
                View all <ChevronRight size={12} />
              </button>
            </div>
            {activeCampaigns.length === 0 ? (
              <div className="ma-empty-inline">
                <Megaphone size={20} />
                <span>No active campaigns</span>
                <button
                  type="button"
                  className="ma-btn ma-btn-secondary"
                  onClick={() => setCampaignModal({ open: true, campaign: null })}
                >
                  <Plus size={13} /> Create campaign
                </button>
              </div>
            ) : (
              <ul className="ma-mini-list">
                {activeCampaigns.slice(0, 5).map(campaign => (
                  <li key={campaign.id}>
                    <span className="ma-avatar-type">
                      {campaign.type === 'TEXT' ? <Type size={14} /> : <Image size={14} />}
                    </span>
                    <span className="ma-mini-text">
                      <strong>{campaign.headline || 'Untitled'}</strong>
                      <em>{campaign.client?.companyName}</em>
                    </span>
                    <span className="ma-mini-stat">{formatNumber(campaign.totalViews || 0)} views</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </>
    );
  };

  const renderClients = () => (
    <>
      <section className="ma-card">
        <div className="ma-card-head">
          <h2 className="ma-card-title">
            Ad clients <span className="ma-card-count">({clients.length})</span>
          </h2>
          <button
            type="button"
            className="ma-btn ma-btn-primary"
            onClick={() => setClientModal({ open: true, client: null })}
          >
            <Plus size={13} /> <span className="ma-btn-text">Add client</span>
          </button>
        </div>

        {clients.length === 0 ? (
          <div className="ma-empty-inline">
            <Users size={22} />
            <span>Add businesses that want to advertise on your storefront.</span>
            <button
              type="button"
              className="ma-btn ma-btn-primary"
              onClick={() => setClientModal({ open: true, client: null })}
            >
              <Plus size={13} /> Add your first client
            </button>
          </div>
        ) : (
          <ul className="ma-list">
            {clients.map(client => {
              const campaignCount = client._count?.campaigns || 0;
              return (
                <li key={client.id} className="ma-row">
                  <div className="ma-client-row">
                    <span className="ma-avatar">{client.companyName?.charAt(0).toUpperCase() || '?'}</span>
                    <span className="ma-client-info">
                      <strong>{client.companyName}</strong>
                      <em>{client.contactEmail}{client.contactPhone ? ` · ${client.contactPhone}` : ''}</em>
                    </span>
                    <span className="ma-client-count">
                      {campaignCount} campaign{campaignCount === 1 ? '' : 's'}
                    </span>
                    <span className="ma-client-actions">
                      <button
                        type="button"
                        className="ma-icon-btn"
                        title="Create campaign"
                        aria-label="Create campaign"
                        onClick={() => setCampaignModal({ open: true, campaign: { clientId: client.id } })}
                      >
                        <Plus size={14} />
                      </button>
                      <button
                        type="button"
                        className="ma-icon-btn"
                        title="Edit"
                        aria-label="Edit client"
                        onClick={() => setClientModal({ open: true, client })}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        type="button"
                        className="ma-icon-btn ma-icon-btn-danger"
                        title="Delete"
                        aria-label="Delete client"
                        onClick={() => handleDeleteClient(client.id)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </>
  );

  const renderCampaigns = () => {
    const selectableCampaigns = campaigns.filter(c => !c.isTemplate);
    const allSelected =
      selectableCampaigns.length > 0 &&
      selectedCampaigns.length === selectableCampaigns.length;

    return (
      <>
        <section className="ma-card">
          <div className="ma-card-head">
            <h2 className="ma-card-title">
              Ad campaigns <span className="ma-card-count">({campaigns.length})</span>
            </h2>
            <div className="ma-card-actions">
              {templates.length > 0 && (
                <select
                  className="ma-input ma-select ma-card-select"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      const template = templates.find(t => t.id === e.target.value);
                      if (template) {
                        setCampaignModal({
                          open: true,
                          campaign: {
                            ...template,
                            id: null,
                            isTemplate: false,
                            templateName: null,
                            headline: template.headline || '',
                            status: 'DRAFT'
                          }
                        });
                      }
                      e.target.value = '';
                    }
                  }}
                  aria-label="Use template"
                >
                  <option value="">Use template…</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.templateName || t.headline}</option>
                  ))}
                </select>
              )}
              <button
                type="button"
                className="ma-btn ma-btn-primary"
                onClick={() => setCampaignModal({ open: true, campaign: null })}
                disabled={clients.length === 0}
              >
                <Plus size={13} /> <span className="ma-btn-text">New campaign</span>
              </button>
            </div>
          </div>

          {selectedCampaigns.length > 0 && (
            <div className="ma-bulk">
              <span className="ma-bulk-count">{selectedCampaigns.length} selected</span>
              <div className="ma-bulk-actions">
                <button
                  type="button"
                  className="ma-btn ma-btn-secondary"
                  onClick={() => handleBulkAction('pause')}
                  disabled={actionLoading === 'bulk-pause'}
                >
                  <Pause size={13} /> Pause
                </button>
                <button
                  type="button"
                  className="ma-btn ma-btn-secondary"
                  onClick={() => handleBulkAction('resume')}
                  disabled={actionLoading === 'bulk-resume'}
                >
                  <Play size={13} /> Resume
                </button>
                <button
                  type="button"
                  className="ma-btn ma-btn-danger"
                  onClick={() => handleBulkAction('delete')}
                  disabled={actionLoading === 'bulk-delete'}
                >
                  <Trash2 size={13} /> Delete
                </button>
                <button
                  type="button"
                  className="ma-btn ma-btn-ghost"
                  onClick={() => setSelectedCampaigns([])}
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {clients.length === 0 ? (
            <div className="ma-empty-inline">
              <AlertCircle size={22} />
              <span>You need at least one client before creating campaigns.</span>
              <button
                type="button"
                className="ma-btn ma-btn-primary"
                onClick={() => setActiveTab('clients')}
              >
                Go to clients
              </button>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="ma-empty-inline">
              <Megaphone size={22} />
              <span>No campaigns yet.</span>
              <button
                type="button"
                className="ma-btn ma-btn-primary"
                onClick={() => setCampaignModal({ open: true, campaign: null })}
              >
                <Plus size={13} /> Create your first campaign
              </button>
            </div>
          ) : (
            <ul className="ma-list">
              {campaigns.map(campaign => {
                const status = (campaign.status || '').toLowerCase();
                const ctr = campaign.totalViews > 0
                  ? ((campaign.totalClicks / campaign.totalViews) * 100).toFixed(2) + '%'
                  : '0%';
                const isSelected = selectedCampaigns.includes(campaign.id);
                return (
                  <li key={campaign.id} className="ma-row">
                    <div className="ma-campaign-row">
                      <label className="ma-check" title="Select campaign">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectCampaign(campaign.id)}
                          aria-label="Select campaign"
                        />
                      </label>
                      <span className="ma-campaign-name">
                        <strong>{campaign.headline || 'Untitled'}</strong>
                        <em>{campaign.client?.companyName || 'No client'} · {formatDate(campaign.startDate)} – {formatDate(campaign.endDate)}</em>
                      </span>
                      <span className="ma-campaign-meta">
                        <span className="ma-pill ma-pill-muted">
                          {campaign.type === 'TEXT' ? <Type size={11} /> : <Image size={11} />}
                          {campaign.type}
                        </span>
                        <span className="ma-pill ma-pill-muted">
                          {(campaign.placement || '').replace(/_/g, ' ')}
                        </span>
                        <span className={`ma-pill ${PILL_FOR_STATUS[status] || 'ma-pill-muted'}`}>
                          {campaign.status}
                        </span>
                      </span>
                      <span className="ma-campaign-stats">
                        <strong>{formatNumber(campaign.totalViews || 0)}</strong> views ·
                        {' '}{formatNumber(campaign.totalClicks || 0)} clicks ·
                        {' '}{ctr} CTR
                      </span>
                      <span className="ma-campaign-actions">
                        {status === 'active' ? (
                          <button
                            type="button"
                            className="ma-icon-btn"
                            title="Pause"
                            aria-label="Pause campaign"
                            onClick={() => handleCampaignAction(campaign.id, 'pause')}
                          >
                            <Pause size={14} />
                          </button>
                        ) : (status === 'draft' || status === 'paused') ? (
                          <button
                            type="button"
                            className="ma-icon-btn ma-icon-btn-success"
                            title="Activate"
                            aria-label="Activate campaign"
                            onClick={() => handleCampaignAction(campaign.id, 'activate')}
                          >
                            <Play size={14} />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="ma-icon-btn"
                          title="Duplicate"
                          aria-label="Duplicate campaign"
                          onClick={() => handleDuplicateCampaign(campaign.id)}
                        >
                          <CopyIcon size={14} />
                        </button>
                        <button
                          type="button"
                          className="ma-icon-btn"
                          title="Edit"
                          aria-label="Edit campaign"
                          onClick={() => setCampaignModal({ open: true, campaign })}
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          type="button"
                          className="ma-icon-btn"
                          title="Save as template"
                          aria-label="Save as template"
                          onClick={() => handleSaveAsTemplate(campaign.id, campaign.headline || 'Untitled')}
                        >
                          <FileText size={14} />
                        </button>
                        <button
                          type="button"
                          className="ma-icon-btn ma-icon-btn-danger"
                          title="Delete"
                          aria-label="Delete campaign"
                          onClick={() => handleDeleteCampaign(campaign.id)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {selectableCampaigns.length > 0 && (
            <button
              type="button"
              className="ma-select-all-link"
              onClick={handleSelectAll}
            >
              {allSelected ? 'Clear selection' : 'Select all on this page'}
            </button>
          )}
        </section>
      </>
    );
  };

  const handleDownloadReport = async (type = 'all', id = null) => {
    try {
      const axios = getAxios();
      let url = '/merchant/ads/analytics/export';

      if (type === 'campaign' && id) {
        url = `/merchant/ads/analytics/export/${id}`;
      } else if (type === 'client' && id) {
        url = `/merchant/ads/analytics/export/client/${id}`;
      }

      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `ad-report-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Report downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download report');
    }
  };

  const handleEmailReport = async (campaignId) => {
    try {
      setActionLoading('email-' + campaignId);
      const axios = getAxios();
      await axios.post(`/merchant/ads/campaigns/${campaignId}/send-report`);
      toast.success('Report sent to client');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send report');
    } finally {
      setActionLoading(null);
    }
  };

  const renderAnalytics = () => {
    if (!analytics) {
      return (
        <section className="ma-card">
          <div className="ma-empty-inline">
            <BarChart3 size={22} />
            <span>Analytics will appear once your campaigns start getting views.</span>
          </div>
        </section>
      );
    }

    const dailyMax = Math.max(...(analytics.daily || []).map(d => d.views || 0), 1);
    const devices = [
      { key: 'mobile',  label: 'Mobile',  icon: Smartphone },
      { key: 'tablet',  label: 'Tablet',  icon: Tablet },
      { key: 'desktop', label: 'Desktop', icon: Monitor },
    ];

    const severityPill = (severity) => {
      const s = (severity || '').toLowerCase();
      if (s === 'critical' || s === 'high') return 'ma-pill-danger';
      if (s === 'medium') return 'ma-pill-warn';
      return 'ma-pill-muted';
    };

    return (
      <>
        {/* ── 4-tile summary stats ─────────────────────────── */}
        <section className="ma-stat-grid">
          <div className="ma-stat">
            <span className="ma-stat-label">Total views</span>
            <span className="ma-stat-value">{formatNumber(analytics.totals?.views || 0)}</span>
          </div>
          <div className="ma-stat">
            <span className="ma-stat-label">Total clicks</span>
            <span className="ma-stat-value ma-stat-value-success">{formatNumber(analytics.totals?.clicks || 0)}</span>
          </div>
          <div className="ma-stat">
            <span className="ma-stat-label">Avg CTR</span>
            <span className="ma-stat-value">{analytics.totals?.ctr || '0'}%</span>
          </div>
          <div className="ma-stat">
            <span className="ma-stat-label">Campaigns</span>
            <span className="ma-stat-value">{analytics.campaignCount || 0}</span>
          </div>
        </section>

        {/* ── Device breakdown ─────────────────────────────── */}
        {analytics.deviceBreakdown && (
          <section className="ma-card">
            <div className="ma-card-head">
              <h2 className="ma-card-title">Device breakdown</h2>
              <button
                type="button"
                className="ma-btn ma-btn-secondary"
                onClick={handleDownloadReport}
                disabled={!analytics?.daily?.length}
              >
                <Download size={13} /> <span className="ma-btn-text">Export CSV</span>
              </button>
            </div>
            <div className="ma-device-grid">
              {devices.map(({ key, label, icon: Icon }) => {
                const d = analytics.deviceBreakdown[key] || {};
                return (
                  <div key={key} className="ma-device">
                    <div className="ma-device-head">
                      <span className="ma-device-icon"><Icon size={16} /></span>
                      <span className="ma-device-name">{label}</span>
                      {typeof d.viewsPercent === 'number' && (
                        <span className="ma-pill ma-pill-muted">{d.viewsPercent}%</span>
                      )}
                    </div>
                    <dl className="ma-device-stats">
                      <div>
                        <dt>Views</dt>
                        <dd>{formatNumber(d.views || 0)}</dd>
                      </div>
                      <div>
                        <dt>Clicks</dt>
                        <dd>{formatNumber(d.clicks || 0)}</dd>
                      </div>
                      <div>
                        <dt>CTR</dt>
                        <dd>{d.ctr || '0'}%</dd>
                      </div>
                    </dl>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Daily performance bar chart ──────────────────── */}
        {analytics.daily?.length > 0 && (
          <section className="ma-card">
            <div className="ma-card-head">
              <h2 className="ma-card-title">Daily performance (last 14 days)</h2>
              <div className="ma-chart-legend">
                <span><span className="ma-legend-dot ma-legend-views" /> Views</span>
                <span><span className="ma-legend-dot ma-legend-clicks" /> Clicks</span>
              </div>
            </div>
            <div className="ma-chart">
              {analytics.daily.slice(-14).map((day, idx) => (
                <div key={idx} className="ma-chart-col">
                  <div className="ma-chart-bars">
                    <div
                      className="ma-bar ma-bar-views"
                      style={{ height: `${Math.min((day.views / dailyMax) * 100, 100)}%` }}
                      title={`Views: ${day.views}`}
                    />
                    <div
                      className="ma-bar ma-bar-clicks"
                      style={{ height: `${Math.min((day.clicks / dailyMax) * 100, 100)}%` }}
                      title={`Clicks: ${day.clicks}`}
                    />
                  </div>
                  <span className="ma-chart-date">
                    {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Top campaigns ────────────────────────────────── */}
        {analytics.topCampaigns?.length > 0 && (
          <section className="ma-card">
            <div className="ma-card-head">
              <h2 className="ma-card-title">Top campaigns</h2>
            </div>
            <ul className="ma-list">
              {analytics.topCampaigns.map(c => {
                const emailing = actionLoading === 'email-' + c.id;
                return (
                  <li key={c.id} className="ma-row">
                    <div className="ma-perf-row">
                      <span className="ma-perf-name">
                        <strong>{c.name || 'Untitled'}</strong>
                        <em>{c.clientName}</em>
                      </span>
                      <span className="ma-pill ma-pill-muted">
                        {(c.placement || '').replace(/_/g, ' ')}
                      </span>
                      <span className="ma-perf-stats">
                        <strong>{formatNumber(c.views || 0)}</strong> views ·
                        {' '}{formatNumber(c.clicks || 0)} clicks ·
                        {' '}{c.ctr}% CTR
                      </span>
                      <span className="ma-perf-actions">
                        <button
                          type="button"
                          className="ma-icon-btn"
                          title="Download campaign report"
                          aria-label="Download report"
                          onClick={() => handleDownloadReport('campaign', c.id)}
                        >
                          <Download size={14} />
                        </button>
                        <button
                          type="button"
                          className="ma-icon-btn"
                          title="Email report to client"
                          aria-label="Email report"
                          onClick={() => handleEmailReport(c.id)}
                          disabled={emailing}
                        >
                          {emailing ? <Loader2 size={14} className="ma-spin" /> : <Send size={14} />}
                        </button>
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {/* ── Click protection ─────────────────────────────── */}
        <section className="ma-card">
          <div className="ma-card-head">
            <h2 className="ma-card-title">
              <Shield size={14} /> Click protection
            </h2>
          </div>

          {fraudSummary && (
            <div className="ma-stat-grid" style={{ marginBottom: 14 }}>
              <div className="ma-stat">
                <span className="ma-stat-label">Blocked clicks</span>
                <span className="ma-stat-value">{formatNumber(fraudSummary.totalBlockedClicks || 0)}</span>
              </div>
              <div className="ma-stat">
                <span className="ma-stat-label">Bot clicks</span>
                <span className="ma-stat-value">{formatNumber(fraudSummary.totalBotClicks || 0)}</span>
              </div>
              <div className="ma-stat">
                <span className="ma-stat-label">Active alerts</span>
                <span className={`ma-stat-value${fraudSummary.activeAlerts > 0 ? ' ma-stat-value-warn' : ''}`}>
                  {fraudSummary.activeAlerts || 0}
                </span>
              </div>
              <div className="ma-stat">
                <span className="ma-stat-label">Protection rate</span>
                <span className="ma-stat-value ma-stat-value-success">{fraudSummary.protectionRate || '0'}%</span>
              </div>
            </div>
          )}

          {fraudAlerts.length > 0 ? (
            <ul className="ma-list">
              {fraudAlerts.slice(0, 5).map(alert => (
                <li key={alert.id} className="ma-row">
                  <div className="ma-alert-row">
                    <span className={`ma-pill ${severityPill(alert.severity)}`}>
                      {alert.severity}
                    </span>
                    <span className="ma-alert-info">
                      <strong>{alert.campaignName}</strong>
                      <em>
                        {alert.alertType?.replace(/_/g, ' ')}
                        {alert.details?.clickCount && ` · ${alert.details.clickCount} suspicious clicks`}
                        {alert.details?.timeWindow && ` in ${alert.details.timeWindow}`}
                      </em>
                    </span>
                    <span className="ma-alert-time">
                      {new Date(alert.createdAt).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                    <button
                      type="button"
                      className="ma-icon-btn"
                      title="Dismiss alert"
                      aria-label="Dismiss alert"
                      onClick={() => handleDismissFraudAlert(alert.id)}
                    >
                      <X size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="ma-empty-inline">
              <Shield size={20} />
              <span>No fraud alerts. Your campaigns are protected.</span>
            </div>
          )}
        </section>
      </>
    );
  };

  // Settings handlers
  const handleTogglePlacement = async (placement) => {
    try {
      const axios = getAxios();
      const currentPlacements = settings?.enabledPlacements || [];
      let newPlacements;

      if (currentPlacements.includes(placement)) {
        newPlacements = currentPlacements.filter(p => p !== placement);
      } else {
        newPlacements = [...currentPlacements, placement];
      }

      const response = await axios.put('/merchant/ads/settings', {
        enabledPlacements: newPlacements
      });

      if (response.data.success) {
        setSettings(prev => ({ ...prev, enabledPlacements: newPlacements }));
        toast.success('Placements updated');
      }
    } catch (error) {
      console.error('Error updating placements:', error);
      toast.error('Failed to update placements');
    }
  };

  const handleSaveBlurbSettings = async () => {
    try {
      const axios = getAxios();
      const response = await axios.put('/merchant/ads/settings', {
        showAdvertiseBlurb: settings?.showAdvertiseBlurb,
        advertiseBlurbText: settings?.advertiseBlurbText,
        advertiseContactEmail: settings?.advertiseContactEmail
      });

      if (response.data.success) {
        toast.success('Blurb settings saved');
      }
    } catch (error) {
      console.error('Error saving blurb settings:', error);
      toast.error('Failed to save settings');
    }
  };

  const handleSavePricingTable = async () => {
    try {
      const axios = getAxios();
      const response = await axios.put('/merchant/ads/pricing-table', pricingTable);

      if (response.data.success) {
        toast.success('Pricing table saved');
      }
    } catch (error) {
      console.error('Error saving pricing table:', error);
      toast.error('Failed to save pricing table');
    }
  };

  const renderSettings = () => {
    const PLACEMENTS = [
      'HOMEPAGE_HERO', 'HOMEPAGE_BANNER', 'SHOP_INLINE',
      'SHOP_SIDEBAR', 'CATEGORY_INLINE', 'ABOUT_BANNER',
    ];
    const advertiseUrl = store?.slug
      ? `${store.customDomain ? `https://${store.customDomain}` : getStoreUrl(store.slug)}/advertise`
      : null;

    return (
      <>
        {/* ── Feature Access (not collapsible) ─────────────── */}
        <section className="ma-card">
          <div className="ma-card-head">
            <h2 className="ma-card-title">Feature access</h2>
            <a href="/settings" className="ma-btn ma-btn-ghost">
              Manage subscription <ChevronRight size={12} />
            </a>
          </div>
          <dl className="ma-kv">
            <div className="ma-kv-row">
              <dt>Current plan</dt>
              <dd>
                <span className="ma-pill ma-pill-success">
                  {billing?.tier || access.merchantTier || 'FREE'}
                </span>
              </dd>
            </div>
            <div className="ma-kv-row">
              <dt>Status</dt>
              <dd>
                <span className={`ma-pill ${billing?.isOnTrial ? 'ma-pill-warn' : 'ma-pill-success'}`}>
                  {billing?.isOnTrial ? 'Free trial' : billing?.status || 'Active'}
                </span>
              </dd>
            </div>
            {billing?.isOnTrial && billing?.trialEndsAt && (
              <div className="ma-kv-row">
                <dt>Trial ends</dt>
                <dd>{formatDate(billing.trialEndsAt)}</dd>
              </div>
            )}
            {!billing?.isOnTrial && billing?.endDate && (
              <div className="ma-kv-row">
                <dt>Renews / expires</dt>
                <dd>{formatDate(billing.endDate)}</dd>
              </div>
            )}
            <div className="ma-kv-row">
              <dt>Access type</dt>
              <dd>{access.hasTierAccess ? 'Included in plan' : 'Not available'}</dd>
            </div>
          </dl>
        </section>

        {/* ── Advertise blurb (collapsible) ───────────────── */}
        <section className="ma-card">
          <button
            type="button"
            className="ma-collapsible-head"
            onClick={() => toggleCard('blurb')}
            aria-expanded={!collapsedCards.blurb}
          >
            <h2 className="ma-card-title">Advertise with us blurb</h2>
            {collapsedCards.blurb ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
          {!collapsedCards.blurb && (
            <div className="ma-collapsible-body">
              <p className="ma-card-desc">
                Show an "Advertise with us" message on your storefront to attract advertisers.
              </p>

              <label className="ma-switch-row">
                <span className="ma-switch">
                  <input
                    type="checkbox"
                    checked={!!settings?.showAdvertiseBlurb}
                    onChange={e => setSettings(prev => ({ ...prev, showAdvertiseBlurb: e.target.checked }))}
                  />
                  <span className="ma-switch-track" />
                </span>
                <span className="ma-switch-label">Enable blurb on storefront</span>
              </label>

              {settings?.showAdvertiseBlurb && (
                <div className="ma-form-row">
                  <div className="ma-field">
                    <label className="ma-field-label">Blurb text</label>
                    <input
                      className="ma-input"
                      type="text"
                      value={settings?.advertiseBlurbText || 'Advertise with us!'}
                      onChange={e => setSettings(prev => ({ ...prev, advertiseBlurbText: e.target.value }))}
                      placeholder="Advertise with us!"
                    />
                  </div>
                  <div className="ma-field">
                    <label className="ma-field-label">Contact email</label>
                    <input
                      className="ma-input"
                      type="email"
                      value={settings?.advertiseContactEmail || ''}
                      onChange={e => setSettings(prev => ({ ...prev, advertiseContactEmail: e.target.value }))}
                      placeholder="ads@yourstore.com"
                    />
                  </div>
                </div>
              )}

              <div className="ma-card-footer">
                <button
                  type="button"
                  className="ma-btn ma-btn-primary"
                  onClick={handleSaveBlurbSettings}
                >
                  Save blurb settings
                </button>
              </div>
            </div>
          )}
        </section>

        {/* ── Pricing table (collapsible) ─────────────────── */}
        <section className="ma-card">
          <button
            type="button"
            className="ma-collapsible-head"
            onClick={() => toggleCard('pricing')}
            aria-expanded={!collapsedCards.pricing}
          >
            <h2 className="ma-card-title">Ad pricing table</h2>
            {collapsedCards.pricing ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
          {!collapsedCards.pricing && (
            <div className="ma-collapsible-body">
              <p className="ma-card-desc">
                Set your ad pricing and make it public for potential advertisers.
              </p>

              <label className="ma-switch-row">
                <span className="ma-switch">
                  <input
                    type="checkbox"
                    checked={!!pricingTable?.isPublic}
                    onChange={e => setPricingTable(prev => ({ ...prev, isPublic: e.target.checked }))}
                  />
                  <span className="ma-switch-track" />
                </span>
                <span className="ma-switch-label">Make pricing publicly visible</span>
              </label>

              {pricingTable?.isPublic && advertiseUrl && (
                <div className="ma-field">
                  <label className="ma-field-label">Shareable pricing page</label>
                  <div className="ma-share-row">
                    <input
                      className="ma-input"
                      type="text"
                      readOnly
                      value={advertiseUrl}
                    />
                    <button
                      type="button"
                      className="ma-icon-btn"
                      title="Copy link"
                      aria-label="Copy link"
                      onClick={() => {
                        navigator.clipboard.writeText(advertiseUrl);
                        toast.success('Link copied');
                      }}
                    >
                      <Copy size={14} />
                    </button>
                    <button
                      type="button"
                      className="ma-icon-btn"
                      title="Open page"
                      aria-label="Open page"
                      onClick={() => window.open(advertiseUrl, '_blank')}
                    >
                      <ExternalLink size={14} />
                    </button>
                  </div>
                </div>
              )}

              <div className="ma-price-block">
                <h3 className="ma-price-title">
                  <Type size={13} /> Text ad pricing ({accountCurrency})
                </h3>
                <div className="ma-form-row ma-form-row-3">
                  <div className="ma-field">
                    <label className="ma-field-label">Daily</label>
                    <input
                      className="ma-input"
                      type="number"
                      value={pricingTable?.textDaily || ''}
                      onChange={e => setPricingTable(prev => ({ ...prev, textDaily: e.target.value }))}
                      placeholder="0"
                    />
                  </div>
                  <div className="ma-field">
                    <label className="ma-field-label">Weekly</label>
                    <input
                      className="ma-input"
                      type="number"
                      value={pricingTable?.textWeekly || ''}
                      onChange={e => setPricingTable(prev => ({ ...prev, textWeekly: e.target.value }))}
                      placeholder="0"
                    />
                  </div>
                  <div className="ma-field">
                    <label className="ma-field-label">Monthly</label>
                    <input
                      className="ma-input"
                      type="number"
                      value={pricingTable?.textMonthly || ''}
                      onChange={e => setPricingTable(prev => ({ ...prev, textMonthly: e.target.value }))}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              <div className="ma-price-block">
                <h3 className="ma-price-title">
                  <Image size={13} /> Banner ad pricing ({accountCurrency})
                </h3>
                <div className="ma-form-row ma-form-row-3">
                  <div className="ma-field">
                    <label className="ma-field-label">Daily</label>
                    <input
                      className="ma-input"
                      type="number"
                      value={pricingTable?.bannerDaily || ''}
                      onChange={e => setPricingTable(prev => ({ ...prev, bannerDaily: e.target.value }))}
                      placeholder="0"
                    />
                  </div>
                  <div className="ma-field">
                    <label className="ma-field-label">Weekly</label>
                    <input
                      className="ma-input"
                      type="number"
                      value={pricingTable?.bannerWeekly || ''}
                      onChange={e => setPricingTable(prev => ({ ...prev, bannerWeekly: e.target.value }))}
                      placeholder="0"
                    />
                  </div>
                  <div className="ma-field">
                    <label className="ma-field-label">Monthly</label>
                    <input
                      className="ma-input"
                      type="number"
                      value={pricingTable?.bannerMonthly || ''}
                      onChange={e => setPricingTable(prev => ({ ...prev, bannerMonthly: e.target.value }))}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              <div className="ma-field">
                <label className="ma-field-label">Contact email (for inquiries)</label>
                <input
                  className="ma-input"
                  type="email"
                  value={pricingTable?.contactEmail || ''}
                  onChange={e => setPricingTable(prev => ({ ...prev, contactEmail: e.target.value }))}
                  placeholder="ads@yourstore.com"
                />
              </div>
              <div className="ma-field">
                <label className="ma-field-label">Custom note</label>
                <textarea
                  className="ma-input ma-textarea"
                  value={pricingTable?.customNote || ''}
                  onChange={e => setPricingTable(prev => ({ ...prev, customNote: e.target.value }))}
                  placeholder="Additional pricing info, discounts, or terms…"
                  rows={3}
                />
              </div>

              <div className="ma-card-footer">
                <button
                  type="button"
                  className="ma-btn ma-btn-primary"
                  onClick={handleSavePricingTable}
                >
                  Save pricing table
                </button>
              </div>
            </div>
          )}
        </section>

        {/* ── Enabled placements ──────────────────────────── */}
        <section className="ma-card">
          <div className="ma-card-head">
            <h2 className="ma-card-title">Enabled placements</h2>
          </div>
          <p className="ma-card-desc">
            Choose where ads can appear on your storefront.
          </p>
          <div className="ma-chip-grid">
            {PLACEMENTS.map(placement => {
              const enabled = settings?.enabledPlacements?.includes(placement) || false;
              return (
                <label
                  key={placement}
                  className={`ma-chip${enabled ? ' is-selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={() => handleTogglePlacement(placement)}
                  />
                  <span>{placement.replace(/_/g, ' ')}</span>
                </label>
              );
            })}
          </div>
        </section>

        {/* ── Banner sizes reference ──────────────────────── */}
        <section className="ma-card">
          <div className="ma-card-head">
            <h2 className="ma-card-title">Supported banner sizes</h2>
          </div>
          <p className="ma-card-desc">Industry standard sizes for banner ads.</p>
          <ul className="ma-size-list">
            {Object.entries(bannerSizes || {}).map(([key, size]) => (
              <li key={key} className="ma-size-chip">
                <strong>{size.label}</strong>
                <em>{size.width} × {size.height}</em>
              </li>
            ))}
          </ul>
        </section>
      </>
    );
  };

  // =========================================
  // MODALS
  // =========================================

  const ClientModal = () => {
    const [formData, setFormData] = useState({
      companyName: clientModal.client?.companyName || '',
      contactEmail: clientModal.client?.contactEmail || '',
      contactPhone: clientModal.client?.contactPhone || '',
      notes: clientModal.client?.notes || ''
    });

    const close = () => setClientModal({ open: false, client: null });
    const isSaving = actionLoading === 'client';
    const canSave = formData.companyName.trim() && formData.contactEmail.trim();

    return (
      <Modal
        isOpen={clientModal.open}
        onClose={close}
        title={clientModal.client ? 'Edit client' : 'Add client'}
        size="md"
        padded={false}
        footer={
          <>
            <button type="button" className="ma-btn ma-btn-secondary" onClick={close} disabled={isSaving}>
              Cancel
            </button>
            <button
              type="submit"
              form="ma-client-form"
              className="ma-btn ma-btn-primary"
              disabled={!canSave || isSaving}
            >
              {isSaving ? <Loader2 size={14} className="ma-spin" /> : null}
              {isSaving ? 'Saving…' : 'Save client'}
            </button>
          </>
        }
      >
        <form
          id="ma-client-form"
          className="ma-form"
          onSubmit={(e) => { e.preventDefault(); handleSaveClient(formData); }}
        >
          <div className="ma-field">
            <label className="ma-field-label">Company name *</label>
            <input
              className="ma-input"
              type="text"
              value={formData.companyName}
              onChange={e => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
              placeholder="Business name"
              required
            />
          </div>
          <div className="ma-form-row">
            <div className="ma-field">
              <label className="ma-field-label">Contact email *</label>
              <input
                className="ma-input"
                type="email"
                value={formData.contactEmail}
                onChange={e => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
                placeholder="email@example.com"
                required
              />
            </div>
            <div className="ma-field">
              <label className="ma-field-label">Contact phone</label>
              <input
                className="ma-input"
                type="tel"
                value={formData.contactPhone}
                onChange={e => setFormData(prev => ({ ...prev, contactPhone: e.target.value }))}
                placeholder="+234…"
              />
            </div>
          </div>
          <div className="ma-field">
            <label className="ma-field-label">Notes</label>
            <textarea
              className="ma-input ma-textarea"
              value={formData.notes}
              onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Internal notes about this client…"
            />
          </div>
        </form>
      </Modal>
    );
  };

  const CampaignModal = () => {
    const [formData, setFormData] = useState({
      clientId: campaignModal.campaign?.clientId || '',
      name: campaignModal.campaign?.name || '',
      type: campaignModal.campaign?.type || 'TEXT',
      headline: campaignModal.campaign?.headline || '',
      description: campaignModal.campaign?.description || '',
      ctaText: campaignModal.campaign?.ctaText || 'Learn More',
      ctaLink: campaignModal.campaign?.ctaLink || '',
      mediaUrl: campaignModal.campaign?.mediaUrl || '',
      mediaSize: campaignModal.campaign?.mediaSize || '',
      backgroundColor: campaignModal.campaign?.backgroundColor || '#f8fafc',
      textColor: campaignModal.campaign?.textColor || '#1e293b',
      accentColor: campaignModal.campaign?.accentColor || '#0d7463',
      placement: campaignModal.campaign?.placement || 'HOMEPAGE_BANNER',
      duration: campaignModal.campaign?.duration || 'ONE_WEEK',
      startDate: campaignModal.campaign?.startDate?.split('T')[0] || new Date().toISOString().split('T')[0],
      startTime: campaignModal.campaign?.startTime || '',
      endTime: campaignModal.campaign?.endTime || '',
      price: campaignModal.campaign?.price || ''
    });

    const close = () => setCampaignModal({ open: false, campaign: null });
    const isSaving = actionLoading === 'campaign';
    const canSave = formData.clientId && formData.name && formData.headline && formData.price;

    return (
      <Modal
        isOpen={campaignModal.open}
        onClose={close}
        title={campaignModal.campaign?.id ? 'Edit campaign' : 'New campaign'}
        size="lg"
        padded={false}
        footer={
          <>
            <button type="button" className="ma-btn ma-btn-secondary" onClick={close} disabled={isSaving}>
              Cancel
            </button>
            <button
              type="submit"
              form="ma-campaign-form"
              className="ma-btn ma-btn-primary"
              disabled={!canSave || isSaving}
            >
              {isSaving ? <Loader2 size={14} className="ma-spin" /> : null}
              {isSaving ? 'Saving…' : 'Save campaign'}
            </button>
          </>
        }
      >
        <form
          id="ma-campaign-form"
          className="ma-form"
          onSubmit={(e) => { e.preventDefault(); handleSaveCampaign(formData); }}
        >
          <div className="ma-form-row">
            <div className="ma-field">
              <label className="ma-field-label">Client *</label>
              <select
                className="ma-input ma-select"
                value={formData.clientId}
                onChange={e => setFormData(prev => ({ ...prev, clientId: e.target.value }))}
                required
              >
                <option value="">Select client…</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.companyName}</option>
                ))}
              </select>
            </div>
            <div className="ma-field">
              <label className="ma-field-label">Campaign name *</label>
              <input
                className="ma-input"
                type="text"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Summer Sale Banner"
                required
              />
            </div>
          </div>

          <div className="ma-form-row">
            <div className="ma-field">
              <label className="ma-field-label">Ad type *</label>
              <select
                className="ma-input ma-select"
                value={formData.type}
                onChange={e => setFormData(prev => ({ ...prev, type: e.target.value }))}
              >
                <option value="TEXT">Text ad</option>
                <option value="BANNER">Banner ad</option>
              </select>
            </div>
            <div className="ma-field">
              <label className="ma-field-label">Placement *</label>
              <select
                className="ma-input ma-select"
                value={formData.placement}
                onChange={e => setFormData(prev => ({ ...prev, placement: e.target.value }))}
              >
                <option value="HOMEPAGE_HERO">Homepage hero</option>
                <option value="HOMEPAGE_BANNER">Homepage banner</option>
                <option value="SHOP_INLINE">Shop inline</option>
                <option value="SHOP_SIDEBAR">Shop sidebar</option>
                <option value="CATEGORY_INLINE">Category inline</option>
                <option value="ABOUT_BANNER">About page banner</option>
              </select>
            </div>
          </div>

          <div className="ma-field">
            <label className="ma-field-label">Headline *</label>
            <input
              className="ma-input"
              type="text"
              value={formData.headline}
              onChange={e => setFormData(prev => ({ ...prev, headline: e.target.value }))}
              placeholder="Attention-grabbing headline"
              maxLength={100}
              required
            />
          </div>

          <div className="ma-field">
            <label className="ma-field-label">Description</label>
            <textarea
              className="ma-input ma-textarea"
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of the ad"
              maxLength={250}
            />
          </div>

          <div className="ma-form-row">
            <div className="ma-field">
              <label className="ma-field-label">CTA button text</label>
              <input
                className="ma-input"
                type="text"
                value={formData.ctaText}
                onChange={e => setFormData(prev => ({ ...prev, ctaText: e.target.value }))}
                placeholder="Learn more"
              />
            </div>
            <div className="ma-field">
              <label className="ma-field-label">CTA link *</label>
              <input
                className="ma-input"
                type="url"
                value={formData.ctaLink}
                onChange={e => setFormData(prev => ({ ...prev, ctaLink: e.target.value }))}
                placeholder="https://…"
              />
            </div>
          </div>

          {formData.type === 'BANNER' && (
            <>
              <div className="ma-field">
                <label className="ma-field-label">Banner image URL *</label>
                <input
                  className="ma-input"
                  type="url"
                  value={formData.mediaUrl}
                  onChange={e => setFormData(prev => ({ ...prev, mediaUrl: e.target.value }))}
                  placeholder="https://…"
                />
              </div>
              <div className="ma-field">
                <label className="ma-field-label">Banner size *</label>
                <select
                  className="ma-input ma-select"
                  value={formData.mediaSize}
                  onChange={e => setFormData(prev => ({ ...prev, mediaSize: e.target.value }))}
                >
                  <option value="">Select size…</option>
                  {Object.entries(bannerSizes || {}).map(([key, size]) => (
                    <option key={key} value={`${size.width}x${size.height}`}>
                      {size.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ma-form-row">
                <div className="ma-field">
                  <label className="ma-field-label">Overlay text color</label>
                  <input
                    className="ma-input ma-color"
                    type="color"
                    value={formData.textColor}
                    onChange={e => setFormData(prev => ({ ...prev, textColor: e.target.value }))}
                  />
                </div>
                <div className="ma-field">
                  <label className="ma-field-label">Button color</label>
                  <input
                    className="ma-input ma-color"
                    type="color"
                    value={formData.accentColor}
                    onChange={e => setFormData(prev => ({ ...prev, accentColor: e.target.value }))}
                  />
                </div>
              </div>
            </>
          )}

          {formData.type === 'TEXT' && (
            <div className="ma-form-row">
              <div className="ma-field">
                <label className="ma-field-label">Background</label>
                <input
                  className="ma-input ma-color"
                  type="color"
                  value={formData.backgroundColor}
                  onChange={e => setFormData(prev => ({ ...prev, backgroundColor: e.target.value }))}
                />
              </div>
              <div className="ma-field">
                <label className="ma-field-label">Text color</label>
                <input
                  className="ma-input ma-color"
                  type="color"
                  value={formData.textColor}
                  onChange={e => setFormData(prev => ({ ...prev, textColor: e.target.value }))}
                />
              </div>
              <div className="ma-field">
                <label className="ma-field-label">Button color</label>
                <input
                  className="ma-input ma-color"
                  type="color"
                  value={formData.accentColor}
                  onChange={e => setFormData(prev => ({ ...prev, accentColor: e.target.value }))}
                />
              </div>
            </div>
          )}

          <div className="ma-form-row">
            <div className="ma-field">
              <label className="ma-field-label">Duration *</label>
              <select
                className="ma-input ma-select"
                value={formData.duration}
                onChange={e => setFormData(prev => ({ ...prev, duration: e.target.value }))}
              >
                <option value="ONE_DAY">1 day</option>
                <option value="ONE_WEEK">1 week</option>
                <option value="ONE_MONTH">1 month</option>
              </select>
            </div>
            <div className="ma-field">
              <label className="ma-field-label">Start date *</label>
              <input
                className="ma-input"
                type="date"
                value={formData.startDate}
                onChange={e => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>
          </div>

          <div className="ma-form-row">
            <div className="ma-field">
              <label className="ma-field-label">Start time (optional)</label>
              <input
                className="ma-input"
                type="time"
                value={formData.startTime}
                onChange={e => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
              />
              <span className="ma-field-hint">Leave empty to start at midnight.</span>
            </div>
            <div className="ma-field">
              <label className="ma-field-label">End time (optional)</label>
              <input
                className="ma-input"
                type="time"
                value={formData.endTime}
                onChange={e => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
              />
              <span className="ma-field-hint">Leave empty to end at 11:59 PM.</span>
            </div>
          </div>

          <div className="ma-field">
            <label className="ma-field-label">Price ({accountCurrency})</label>
            <input
              className="ma-input"
              type="number"
              value={formData.price}
              onChange={e => setFormData(prev => ({ ...prev, price: e.target.value }))}
              placeholder="Amount charged to client"
              required
            />
          </div>
        </form>
      </Modal>
    );
  };

  // =========================================
  // MAIN RENDER
  // =========================================

  const TABS = [
    { id: 'overview',  label: 'Overview',  icon: TrendingUp },
    { id: 'clients',   label: 'Clients',   icon: Users },
    { id: 'campaigns', label: 'Campaigns', icon: Megaphone },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'settings',  label: 'Settings',  icon: Settings },
  ];

  const planLabel = billing?.tier || access.merchantTier || 'FREE';
  const planExpiry = billing?.isOnTrial && billing?.trialEndsAt
    ? `Trial ends ${formatDate(billing.trialEndsAt)}`
    : billing?.endDate
      ? `Renews ${formatDate(billing.endDate)}`
      : 'Included in your plan';

  return (
    <Shell>
      <div className="ma-page">
        <header className="ma-header">
          <div className="ma-header-text">
            <h1 className="ma-title">Storefront Ads</h1>
            <p className="ma-subtitle">Earn from ad placements on your storefront.</p>
          </div>
          <div className="ma-header-actions">
            <span className="ma-plan-pill" title={planExpiry}>
              <CheckCircle size={13} /> {planLabel} · {planExpiry}
            </span>
          </div>
        </header>

        <div className="ma-tabs-wrap">
          <div className="ma-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  type="button"
                  className={`ma-tab${activeTab === t.id ? ' is-active' : ''}`}
                  onClick={() => setActiveTab(t.id)}
                >
                  <Icon size={14} /> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'clients' && renderClients()}
          {activeTab === 'campaigns' && renderCampaigns()}
          {activeTab === 'analytics' && renderAnalytics()}
          {activeTab === 'settings' && renderSettings()}
        </div>

        {/* Modals */}
        {clientModal.open && <ClientModal />}
        {campaignModal.open && <CampaignModal />}

        {/* Template Name Modal */}
        <Modal
          isOpen={templateModal.open}
          onClose={() => setTemplateModal({ open: false, campaignId: null, campaignName: '' })}
          title="Save as template"
          size="sm"
          padded={false}
          footer={
            <>
              <button
                type="button"
                className="ma-btn ma-btn-secondary"
                onClick={() => setTemplateModal({ open: false, campaignId: null, campaignName: '' })}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="ma-template-form"
                className="ma-btn ma-btn-primary"
                disabled={!templateNameInput.trim() || actionLoading === 'template-' + templateModal.campaignId}
              >
                {actionLoading === 'template-' + templateModal.campaignId
                  ? <Loader2 size={14} className="ma-spin" />
                  : null}
                {actionLoading === 'template-' + templateModal.campaignId ? 'Saving…' : 'Save template'}
              </button>
            </>
          }
        >
          <form
            id="ma-template-form"
            className="ma-form"
            onSubmit={(e) => { e.preventDefault(); handleConfirmSaveTemplate(); }}
          >
            <p className="ma-form-help">
              Save <strong>{templateModal.campaignName || 'this campaign'}</strong> as a reusable
              template for future campaigns.
            </p>
            <div className="ma-field">
              <label className="ma-field-label">Template name</label>
              <input
                className="ma-input"
                type="text"
                value={templateNameInput}
                onChange={e => setTemplateNameInput(e.target.value)}
                placeholder="Enter template name"
                autoFocus
              />
            </div>
          </form>
        </Modal>
      </div>
    </Shell>
  );
};

export default MerchantAds;
